import { mdiChevronDown, mdiChevronUp, mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useState } from "react";
import { useOrderCreateCtx } from "../../../../context_providers/order_create_context";
import { currencyFormat } from "../../../../utils/currency_format";
import { useHistory } from "react-router-dom";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";

interface BottomSheetWidgetProps {}

const BottomSheetWidget: FunctionComponent<BottomSheetWidgetProps> = () => {
  const { order, onCreateOrder, useBal, balTxnAmt, billing, reset } =
    useOrderCreateCtx();
  const [expanded, setExpanded] = useState(false);
  const [isOrderCreating, setIsOrderCreating] = useState(false);
  const navigation = useHistory();
  const { showToast } = useToast();

  const currency = order.currency || { short_name: "INR", symbol: "₹" };
  const isNotINR = currency.short_name !== "INR";
  const exchangeRate = order.currency_to_inr || 1;

  const total = order.subtotal + order.excess - order.discount;
  const balance = total - (order.paid + (useBal ? balTxnAmt : 0));

  const totalInINR = total * exchangeRate;
  const balanceInINR = balance * exchangeRate;

  const disable =
    !billing ||
    (order.sale_type !== "Transfer order" &&
      order.products.length === 0 &&
      order.services.length === 0 &&
      order.raw_materials.length === 0);

  const handleCreateOrderClick = async () => {
    if (isOrderCreating || disable) return;

    // Check if org is required for Finished Goods
    const hasFinishedGoods = (order.products ?? []).some(
      (p: any) => p?.item?.subcategory === "Finished Goods",
    );
    const orgSet = !!(order as any)?.org?.namespace;
    if (hasFinishedGoods && !orgSet) {
      showToast({
        type: ToastType.error,
        text: "Assigning Organization for Device (Finished Good) is mandatory",
      });
      return;
    }

    const confirmed = window.confirm("Are you sure to create order?");
    if (!confirmed) return;

    setIsOrderCreating(true);

    try {
      const result = await onCreateOrder();

      if (!result?.success) {
        showToast({
          type: ToastType.error,
          text: result?.error || "Order Creation failed",
        });
        setTimeout(() => navigation.push("/orders"), 2000);
        return;
      }

      showToast({ type: ToastType.success, text: result?.message });
      navigation.push("/orders");
    } catch (err) {
      console.error("Order creation failed:", err);
      showToast({ type: ToastType.error, text: "Something went wrong" });
      setTimeout(() => navigation.push("/orders"), 2000);
    } finally {
      setIsOrderCreating(false);
    }
  };

  const isDisabled = disable || isOrderCreating;

  useEffect(() => {}, [currency.short_name, exchangeRate]);

  return (
    <div id="order-create-bottom-sheet">
      {expanded && (
        <>
          <div className="flex justify-between items-center mb-2">
            <p className="font-semibold italic">Order Summary</p>
            <div
              id="order-create-summary-collapse-btn"
              onClick={() => setExpanded(false)}
              className="hover:scale-110 duration-200 transform bg-gray-100 p-1 rounded-full hover:text-myPrimaryColor"
            >
              <Icon path={mdiClose} className="h-4 w-4" />
            </div>
          </div>

          <div className="grid grid-cols-8 gap-1 text-sm">
            <div className="col-span-4">
              <div className="flex justify-between w-full">
                <div className="font-semibold">Subtotal</div>
                <div id="order-create-summary-subtotal">
                  {isNotINR ? (
                    <>
                      {currency.symbol} {currencyFormat(order.subtotal)}{" "}
                      <span className="text-gray-500 text-xs ml-2">
                        (₹{currencyFormat(order.subtotal * exchangeRate)})
                      </span>
                    </>
                  ) : (
                    <>{currencyFormat(order.subtotal, "INR")}</>
                  )}
                </div>
              </div>

              <div className="flex justify-between w-full my-1">
                <div className="font-semibold">Total</div>
                <div
                  id="order-create-summary-total"
                  className="font-semibold"
                >
                  {isNotINR ? (
                    <>
                      {currencyFormat(total, currency.short_name)}
                      <span className="text-gray-500 text-xs ml-2">
                        ({currencyFormat(totalInINR, "INR")})
                      </span>
                    </>
                  ) : (
                    <>{currencyFormat(total, "INR")}</>
                  )}
                </div>
              </div>
            </div>
          </div>
          <hr className="my-2" />
        </>
      )}

      <div className="flex justify-between mt-2">
        <div className="flex gap-2 items-center">
          <p id="order-create-balance-total" className="font-semibold">
            Total:&nbsp;
            {isNotINR ? (
              <>
                {currencyFormat(balance, currency.short_name)}
                <span className="text-gray-500 text-sm ml-1">
                  ({currencyFormat(balanceInINR, "INR")})
                </span>
              </>
            ) : (
              <>{currencyFormat(balance, "INR")}</>
            )}
          </p>

          <span
            id="order-create-summary-toggle-btn"
            onClick={() => setExpanded((o) => !o)}
            className="hover:bg-gray-100 rounded-full p-1 hover:scale-110 transform duration-200 cursor-pointer"
          >
            <Icon
              path={expanded ? mdiChevronDown : mdiChevronUp}
              className="h-5 w-5"
            />
          </span>
        </div>

        <div className="flex gap-5 text-sm">
          <button
            id="order-create-clear-btn"
            onClick={() => {
              if (window.confirm("Are you sure? It cannot be undone.")) reset();
            }}
            className="text-myPrimaryColor"
          >
            Clear
          </button>

          <button
            id="order-create-submit-btn"
            disabled={isDisabled}
            onClick={handleCreateOrderClick}
            className={`${
              disable ? "bg-gray-400 cursor-not-allowed" : "bg-myPrimaryColor"
            } rounded-md px-3 py-1 text-white`}
          >
            {isOrderCreating ? "Creating..." : "Create Order"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BottomSheetWidget;
